import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import DateInput from "../components/DateInput";
import {
  DownloadSimple, FileText, Funnel, CalendarBlank, User, MagnifyingGlass,
  CaretLeft, CaretRight, Spinner
} from "@phosphor-icons/react";

export default function AuditExportPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [entityType, setEntityType] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(0);
  const perPage = 50;

  useEffect(() => {
    API.get("/audit-logs/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fetchPreview = async () => {
    setLogsLoading(true);
    setPage(0);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (userFilter) params.append("user_filter", userFilter);
      if (entityType) params.append("entity_type", entityType);
      if (actionFilter) params.append("action", actionFilter);
      const { data } = await API.get(`/audit-logs/export?${params.toString()}`);
      setLogs(data.logs || []);
      toast.success(`Found ${data.total} audit log entries`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (userFilter) params.append("user_filter", userFilter);
      if (entityType) params.append("entity_type", entityType);
      if (actionFilter) params.append("action", actionFilter);
      params.append("format", format);

      if (format === "csv") {
        const response = await API.get(`/audit-logs/export?${params.toString()}`, { responseType: "blob" });
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const { data } = await API.get(`/audit-logs/export?${params.toString()}`);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setUserFilter("");
    setEntityType("");
    setActionFilter("");
    setLogs([]);
  };

  const paginated = logs.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(logs.length / perPage);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="audit-export-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="audit-export-title">Audit Trail Export</h1>
          <p className="text-sm text-muted-foreground mt-1">Export audit logs for compliance reporting</p>
        </div>
        {stats && (
          <Badge variant="outline" className="text-xs" data-testid="audit-total-count">{stats.total} total records</Badge>
        )}
      </div>

      {/* Filters */}
      <div className="border border-border rounded-md p-4 mb-6 bg-card space-y-4" data-testid="audit-filters">
        <div className="flex items-center gap-2 text-sm font-semibold font-[Chivo]">
          <Funnel size={16} className="text-muted-foreground" />
          Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Start Date</label>
            <DateInput value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 text-xs" data-testid="audit-filter-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">End Date</label>
            <DateInput value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8 text-xs" data-testid="audit-filter-end" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">User</label>
            <Input value={userFilter} onChange={(e) => setUserFilter(e.target.value)} placeholder="Name or ID" className="h-8 text-xs" data-testid="audit-filter-user" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Entity Type</label>
            <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs" data-testid="audit-filter-entity">
              <option value="">All Types</option>
              {stats?.entity_types?.map((et) => (
                <option key={et} value={et}>{et.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Action</label>
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs" data-testid="audit-filter-action">
              <option value="">All Actions</option>
              {stats?.actions?.map((a) => (
                <option key={a} value={a}>{a.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={fetchPreview} disabled={logsLoading} data-testid="audit-preview-btn">
            <MagnifyingGlass size={14} className="mr-1" />
            {logsLoading ? "Searching..." : "Preview"}
          </Button>
          <Button size="sm" variant="outline" onClick={clearFilters} data-testid="audit-clear-btn">Clear</Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => handleExport("csv")} disabled={exporting} data-testid="audit-export-csv-btn">
            <DownloadSimple size={14} className="mr-1" />
            Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleExport("json")} disabled={exporting} data-testid="audit-export-json-btn">
            <DownloadSimple size={14} className="mr-1" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Results */}
      {logs.length > 0 && (
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="audit-results">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-muted/20">
            <span className="text-xs font-semibold">{logs.length} results</span>
            <span className="text-[10px] text-muted-foreground">Page {page + 1} of {totalPages}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-table">
              <thead>
                <tr className="border-b border-border bg-muted/10">
                  <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Timestamp</th>
                  <th className="text-left px-4 py-2 font-semibold text-muted-foreground">User</th>
                  <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Action</th>
                  <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Entity</th>
                  <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {paginated.map((log, i) => (
                  <tr key={log.id || i} className="hover:bg-muted/10" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap font-mono text-[10px]">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-muted-foreground shrink-0" />
                        <span className="font-medium">{log.user_name || "System"}</span>
                        <span className="text-muted-foreground capitalize">({log.user_role})</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="text-[9px] capitalize">{log.action?.replace(/_/g, " ")}</Badge>
                    </td>
                    <td className="px-4 py-2 capitalize text-muted-foreground">{log.entity_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 text-muted-foreground max-w-[300px] truncate">{log.details || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-2.5 border-t border-border flex items-center justify-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="audit-prev-page">
                <CaretLeft size={14} />
              </Button>
              <span className="text-xs text-muted-foreground">{page + 1} / {totalPages}</span>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="audit-next-page">
                <CaretRight size={14} />
              </Button>
            </div>
          )}
        </div>
      )}

      {logs.length === 0 && !logsLoading && (
        <div className="text-center py-16 text-muted-foreground" data-testid="audit-empty">
          <FileText size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">Use the filters above to preview and export audit logs</p>
        </div>
      )}
    </div>
  );
}
